/* eslint-disable react-hooks/rules-of-hooks */
import { h, Fragment } from 'preact'
import { lazy, memo, Suspense, SuspenseList } from 'preact/compat'
import { useState, useEffect, useLayoutEffect, useMemo } from 'preact/hooks'
import { useSelector, useDispatch } from 'react-redux'

import modelTypes from '@constants/modelTypes'
import widgetZones from '@constants/widgetZones'

import withProvider from '@state/withProvider'
import { fetchPage } from '@state/page/pageSlice'

import translate from '@core/translate'
import useSSR from '@core/hooks/useSSR'
import { facetEntities, productsSelector } from '@core/state/page/pageSelectors'

import Breadcrumbs from '@ui/Breadcrumbs/Breadcrumbs'
import { WidgetZoneProvider } from '@ui/WidgetZoneContext'

import { findNestedObject } from '@utils/misc'
import { useSize } from '@utils/dom'

import FadeIn, { fades } from '@components/fadeIn/FadeIn'
import Loader from '@components/loader/Loader'
import NoResults from '@components/noResults/NoResults'
import ProductList from '@components/productList'

import WithSearch from '@features/elastic/core/WithSearchRenderProps'
import SearchProvider from '@features/elastic/core/SearchProvider'

import Layout from '@features/elastic/core/Layout'
import FacetSelections from '@features/elastic/facets/FacetSelections'
import PagingContainer from '@features/elastic/paging/PagingContainer'
import FacetList from '@features/elastic/facets/FacetList'
import NumberOfHits from '@features/elastic/components/NumberOfHits'

import getProductRibbons from '@features/productRibbons'
import getSelectedFacets from '@features/elastic/utils/getSelectedFacets'
import getSearchConfig from '@features/elastic/utils/getSearchConfig'
import getPageBreadcrumbs from '@features/elastic/utils/getPageBreadcrumbs'

import ArchiveFacetsModal from './ArchiveFacetsModal'
import ArchiveHeader from './ArchiveHeader'
import ArchiveDescription from './ArchiveDescription'

const Footer = lazy(() =>
	import(/* webpackChunkName: "footer.chunk" */ 'app/features/footer/components/Footer'),
)

function ArchivePage({ pageType }) {
	const {
		id,
		name,
		description,
		subCategories,
		categoryBreadcrumb,
		categoryNavigation,
		displayCategoryBreadcrumb,
		pagingFilteringContext,
		pictureModel,
		q: query,
	} = useSelector(state => state.currentPage)

	const categories = categoryNavigation?.categories ?? []

	const dispatch = useDispatch()
	const [currentSubCategories, setCurrentSubCategories] = useState(subCategories || [])

	const facets = useSelector(facetEntities)
	const { products } = useSelector(productsSelector)
	const { totalItems } = pagingFilteringContext ?? {}
	const { isDesktop } = useSize()

	const { contentBlocks } = useSSR([modelTypes.categoryCurrentPage, modelTypes.initialState])

	// correct breadcrumbs for page type
	const BreadcrumbsComponent = getPageBreadcrumbs(pageType)

	// config object for SearchProvider
	const config = getSearchConfig(async requestUrl => {
		await dispatch(fetchPage(requestUrl))
	})

	// eslint-disable-next-line arrow-body-style
	const selectedFacets = useMemo(() => {
		return getSelectedFacets(facets)
	}, [facets])

	// Set sub categories before render,
	// helps in reducing CLS.
	useLayoutEffect(() => {
		if (categories.length > 0) {
			const active = findNestedObject(categories ?? [], 'id', id)

			setCurrentSubCategories(active.subCategories)
		}
	}, [categories, id])

	// ProductList components also runs this function whenever their products change, so
	// state changes in this page component is unnecessary.
	useEffect(() => {
		getProductRibbons()
	}, [])

	const showFacets = facets.length > 0
	const showProducts = products && products.length > 0
	const isCategoryListing = products && products.length === 0 && categories.length > 0
	const isSearchPage = pageType === 'search'
	const searchText = query
		? translate('search.numberOfResultsFor', totalItems, `"${query}"`)
		: translate('search.numberOfResults', totalItems)

	return (
		<WidgetZoneProvider contentBlocks={contentBlocks}>
			<SearchProvider config={config}>
				<SuspenseList revealOrder="forwards">
					<Layout
						header={
							<Fragment>
								{BreadcrumbsComponent && BreadcrumbsComponent}

								<ArchiveHeader
									name={pageType == 'search' ? translate('search') : name}
									// imageUrl={pictureModel?.imageUrl ?? ''}
									// imageAlt={pictureModel?.alternateText ?? ''}
									subCategories={currentSubCategories}
									searchText={searchText}
									displaySearchText={isSearchPage}
								/>
							</Fragment>
						}
						bodyContent={
							<Fragment>
								<slot data-widget-zone={widgetZones.categoryBeforeProductList} />

								{isCategoryListing && <slot data-widget-zone={widgetZones.categoryListingTop} />}

								<Suspense fallback={<Loader heights={{ desktop: '32px' }} />}>
									<FadeIn fade={fades.fadeIn}>
										{showProducts && isDesktop && (
											<Fragment>
												{!isSearchPage && <NumberOfHits showQuery />}
												<ProductList products={products} />
											</Fragment>
										)}

										{showProducts && !isDesktop && (
											<Fragment>
												<div className="category-filters">
													<NumberOfHits showQuery />

													{showFacets && (
														<div className="category-filters__filter" data-drawer-toggle="filters">
															<span>Filtrera</span>
															<svg
																xmlns="http://www.w3.org/2000/svg"
																viewBox="0 0 24 24"
																width="24"
																height="24"
															>
																<path fill="none" d="M0 0h24v24H0z" />
																<path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
															</svg>
														</div>
													)}
												</div>

												<ProductList products={products} />
											</Fragment>
										)}

										{!showProducts && (
											<NoResults
												title={translate('category.noresults.title')}
												text={translate('category.noresults.text')}
											/>
										)}
									</FadeIn>
								</Suspense>

								{showProducts && <PagingContainer />}
							</Fragment>
						}
						sideContent={
							isDesktop && (
								<>
									<WithSearch
										mapContextToProps={({ priceRange, setPriceRange, addFacet, removeFacet }) => ({
											priceRange,
											setPriceRange,
											addFacet,
											removeFacet,
										})}
									>
										{({ priceRange, setPriceRange, addFacet, removeFacet }) => {
											const showFacetSelections =
												(priceRange && priceRange.length > 0) ||
												(selectedFacets && selectedFacets.length > 0)

											return (
												<>
													{showFacetSelections && (
														<Suspense fallback={<Loader heights={{ desktop: '50px' }} />}>
															<FacetSelections
																addFacet={addFacet}
																removeFacet={removeFacet}
																priceRange={priceRange}
																setPriceRange={setPriceRange}
																selectedOptions={selectedFacets}
															/>
														</Suspense>
													)}
												</>
											)
										}}
									</WithSearch>

									<Suspense fallback={<Loader heights={{ desktop: '56px' }} />}>
										<FadeIn fade={fades.fadeIn}>
											<FacetList facets={facets} />
										</FadeIn>
									</Suspense>
								</>
							)
						}
						footerContent={
							<>
								{description && <ArchiveDescription description={description} />}

								<ArchiveFacetsModal titleText="Filtrera" buttonText={`Visa ${totalItems} träffar`}>
									<Fragment>
										<WithSearch
											mapContextToProps={({
												priceRange,
												setPriceRange,
												addFacet,
												removeFacet,
											}) => ({
												priceRange,
												setPriceRange,
												addFacet,
												removeFacet,
											})}
										>
											{({ priceRange, setPriceRange, addFacet, removeFacet }) => {
												const showFacetSelections =
													(priceRange && priceRange.length > 0) ||
													(selectedFacets && selectedFacets.length > 0)

												return (
													<>
														{showFacetSelections && (
															<Suspense
																fallback={
																	<Loader
																		heights={{
																			desktop: '50px',
																		}}
																	/>
																}
															>
																<FacetSelections
																	addFacet={addFacet}
																	removeFacet={removeFacet}
																	priceRange={priceRange}
																	setPriceRange={setPriceRange}
																	selectedOptions={selectedFacets}
																/>
															</Suspense>
														)}
													</>
												)
											}}
										</WithSearch>

										<Suspense fallback={<Loader heights={{ desktop: '56px' }} />}>
											<FadeIn fade={fades.fadeIn} className="modal-facets__list">
												<FacetList facets={facets} />
											</FadeIn>
										</Suspense>
									</Fragment>
								</ArchiveFacetsModal>
							</>
						}
					/>
				</SuspenseList>
			</SearchProvider>
		</WidgetZoneProvider>
	)
}

export default withProvider(ArchivePage)
